"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowLeftIcon, CheckIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { submitLead, type Lead } from "@/lib/leads"
import { Button } from "@/components/ui/button"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Wordmark } from "@/components/site/wordmark"
import { OptionCard } from "@/components/onboarding/option-card"
import { QUESTIONS, TOTAL_STEPS } from "@/components/onboarding/questions"

type Contact = {
  prenom: string
  nom: string
  telephone: string
  codePostal: string
  aSavoir: string
}

const EMPTY_CONTACT: Contact = {
  prenom: "",
  nom: "",
  telephone: "",
  codePostal: "",
  aSavoir: "",
}

/** Retrouve l'intitulé lisible d'une réponse, pour le récapitulatif final. */
function labelFor(questionId: string, value: string) {
  const question = QUESTIONS.find((q) => q.id === questionId)
  return question?.choices.find((c) => c.value === value)?.label ?? value
}

function OnboardingFlow() {
  const [step, setStep] = React.useState(0)
  const [answers, setAnswers] = React.useState<Record<string, string[]>>({})
  const [contact, setContact] = React.useState<Contact>(EMPTY_CONTACT)
  const [consent, setConsent] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [status, setStatus] = React.useState<"idle" | "sending" | "done">("idle")

  const headingRef = React.useRef<HTMLHeadingElement>(null)
  const advanceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const isContactStep = step === QUESTIONS.length
  const question = isContactStep ? null : QUESTIONS[step]

  /* Le titre reprend le focus à chaque étape : sans ça, un lecteur d'écran
     reste sur le bouton disparu et n'annonce jamais la question suivante. */
  React.useEffect(() => {
    headingRef.current?.focus()
  }, [step, status])

  React.useEffect(() => {
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current)
    }
  }, [])

  function goNext() {
    setStep((current) => Math.min(current + 1, QUESTIONS.length))
  }

  function goBack() {
    if (advanceTimer.current) clearTimeout(advanceTimer.current)
    setStep((current) => Math.max(current - 1, 0))
  }

  function chooseSingle(questionId: string, value: string) {
    setAnswers((current) => ({ ...current, [questionId]: [value] }))
    /* Court délai avant d'avancer : le temps de voir sa réponse cochée.
       Sans ça, l'écran change avant que le geste soit confirmé à l'œil. */
    if (advanceTimer.current) clearTimeout(advanceTimer.current)
    advanceTimer.current = setTimeout(goNext, 260)
  }

  function toggleMulti(questionId: string, value: string) {
    setAnswers((current) => {
      const selected = current[questionId] ?? []
      return {
        ...current,
        [questionId]: selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value],
      }
    })
  }

  function validate() {
    const found: Record<string, string> = {}
    if (!contact.prenom.trim()) found.prenom = "Indiquez votre prénom."
    if (!contact.nom.trim()) found.nom = "Indiquez votre nom."
    if (contact.telephone.replace(/\D/g, "").length < 9) {
      found.telephone = "Ce numéro semble incomplet."
    }
    if (!/^\d{5}$/.test(contact.codePostal.trim())) {
      found.codePostal = "Cinq chiffres, par exemple 38000."
    }
    if (!consent) found.consent = "Nous avons besoin de votre accord pour vous rappeler."
    setErrors(found)
    return Object.keys(found).length === 0
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!validate()) return

    setStatus("sending")
    const lead: Lead = {
      objectif: answers.objectif?.[0] ?? "",
      depart: answers.depart?.[0] ?? "",
      rythme: answers.rythme?.[0] ?? "",
      creneaux: answers.creneaux ?? [],
      ...contact,
    }
    await submitLead(lead)
    setStatus("done")
  }

  /* ---------------------------------------------------------------- rendu */

  if (status === "done") {
    return (
      <Shell progress={TOTAL_STEPS} total={TOTAL_STEPS}>
        <p className="font-heading text-xs tracking-brand text-faint uppercase">
          Demande envoyée
        </p>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 font-heading text-[clamp(2rem,5vw,3rem)] leading-[1.04] font-medium uppercase outline-none"
        >
          C&apos;est noté, {contact.prenom}.
        </h1>
        <p className="mt-6 max-w-[46ch] text-muted-foreground">
          Votre coach vous rappelle sous 24 h au {contact.telephone}. Dix
          minutes de discussion, puis on cale votre première séance.
        </p>

        <dl className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border">
          <Recap
            terme="Votre objectif"
            valeur={labelFor("objectif", answers.objectif?.[0] ?? "")}
          />
          <Recap
            terme="Votre rythme"
            valeur={labelFor("rythme", answers.rythme?.[0] ?? "")}
          />
          <Recap
            terme="Vos disponibilités"
            /* Séparateur médian plutôt qu'une virgule : les intitulés
               commencent par une majuscule, une énumération se lirait mal. */
            valeur={
              (answers.creneaux ?? [])
                .map((v) => labelFor("creneaux", v))
                .join(" · ") || "À préciser"
            }
          />
        </dl>

        <div className="mt-10">
          <Button size="brand" variant="outline" render={<Link href="/" />}>
            Revenir au site
          </Button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell progress={step + 1} total={TOTAL_STEPS} onBack={step > 0 ? goBack : undefined}>
      <p className="font-heading text-xs tracking-brand text-faint uppercase">
        Étape {step + 1} sur {TOTAL_STEPS}
      </p>

      {question ? (
        <>
          <h1
            ref={headingRef}
            tabIndex={-1}
            id={`question-${question.id}`}
            className="mt-5 font-heading text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.08] font-medium text-balance uppercase outline-none"
          >
            {question.title}
          </h1>
          {question.subtitle ? (
            <p className="mt-4 max-w-[46ch] text-muted-foreground">
              {question.subtitle}
            </p>
          ) : null}

          <div
            role={question.kind === "single" ? "radiogroup" : "group"}
            aria-labelledby={`question-${question.id}`}
            className="mt-9 grid gap-3"
          >
            {question.choices.map((choice) => {
              const selected = (answers[question.id] ?? []).includes(choice.value)
              return (
                <OptionCard
                  key={choice.value}
                  label={choice.label}
                  hint={choice.hint}
                  selected={selected}
                  role={question.kind === "single" ? "radio" : "checkbox"}
                  onClick={() =>
                    question.kind === "single"
                      ? chooseSingle(question.id, choice.value)
                      : toggleMulti(question.id, choice.value)
                  }
                />
              )
            })}
          </div>

          {question.kind === "multi" ? (
            <div className="mt-8">
              <Button
                size="brand"
                onClick={goNext}
                disabled={(answers[question.id] ?? []).length === 0}
              >
                Continuer
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-5 font-heading text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.08] font-medium text-balance uppercase outline-none"
          >
            Où peut-on vous joindre ?
          </h1>
          <p className="mt-4 max-w-[46ch] text-muted-foreground">
            Dernière étape. Votre coach vous rappelle sous 24 h, sans
            engagement de votre part.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-9">
            <FieldGroup>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  id="prenom"
                  label="Prénom"
                  autoComplete="given-name"
                  value={contact.prenom}
                  error={errors.prenom}
                  onChange={(v) => setContact((c) => ({ ...c, prenom: v }))}
                />
                <TextField
                  id="nom"
                  label="Nom"
                  autoComplete="family-name"
                  value={contact.nom}
                  error={errors.nom}
                  onChange={(v) => setContact((c) => ({ ...c, nom: v }))}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  id="telephone"
                  label="Téléphone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="06 12 34 56 78"
                  value={contact.telephone}
                  error={errors.telephone}
                  onChange={(v) => setContact((c) => ({ ...c, telephone: v }))}
                />
                <TextField
                  id="codePostal"
                  label="Code postal"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="38000"
                  value={contact.codePostal}
                  error={errors.codePostal}
                  onChange={(v) => setContact((c) => ({ ...c, codePostal: v }))}
                />
              </div>

              <Field>
                <FieldLabel htmlFor="aSavoir">
                  Quelque chose à savoir avant la première séance ?
                  <span className="ml-1 font-normal text-faint">Facultatif</span>
                </FieldLabel>
                <Textarea
                  id="aSavoir"
                  rows={3}
                  placeholder="Une blessure, une opération, une douleur qui revient…"
                  value={contact.aSavoir}
                  onChange={(e) =>
                    setContact((c) => ({ ...c, aSavoir: e.target.value }))
                  }
                  className="border-border-strong bg-black/45 px-[18px] py-3"
                />
                <p className="text-[0.8125rem] text-faint">
                  Lu par votre coach uniquement, pour adapter les exercices.
                </p>
              </Field>

              <Field>
                <label className="flex items-start gap-3 text-[0.875rem] text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span
                    aria-hidden
                    className="mt-px grid size-5 shrink-0 place-items-center rounded-sm border border-border-strong transition-colors peer-checked:border-foreground peer-checked:bg-foreground peer-focus-visible:ring-3 peer-focus-visible:ring-ring/50 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100"
                  >
                    <CheckIcon className="size-3.5 text-background" />
                  </span>
                  <span>
                    J&apos;accepte d&apos;être rappelé et que ces informations
                    soient transmises à mon coach.
                  </span>
                </label>
                {errors.consent ? <FieldError>{errors.consent}</FieldError> : null}
              </Field>

              <div className="pt-2">
                <Button size="brand" type="submit" disabled={status === "sending"}>
                  {status === "sending" ? "Envoi…" : "Demander à être rappelé"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </>
      )}
    </Shell>
  )
}

/* ------------------------------------------------------------- morceaux */

/** Cadre commun : logotype, fil d'avancement, retour, colonne de lecture. */
function Shell({
  progress,
  total,
  onBack,
  children,
}: {
  progress: number
  total: number
  onBack?: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex min-h-svh flex-col bg-background bg-[radial-gradient(120%_90%_at_50%_0%,rgba(255,255,255,0.07)_0%,rgba(0,0,0,0)_55%)]">
      <header className="mx-auto flex w-full max-w-[620px] items-center justify-between px-gutter pt-8">
        <Link href="/" aria-label="Accueil Cairn">
          <Wordmark className="text-[1.125rem]" />
        </Link>

        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 font-heading text-xs tracking-brand text-faint uppercase transition-colors hover:text-foreground"
          >
            <ArrowLeftIcon className="size-4" />
            Retour
          </button>
        ) : null}
      </header>

      <div
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={progress}
        aria-label="Avancement du questionnaire"
        className="mx-auto mt-8 w-full max-w-[620px] px-gutter"
      >
        <div className="h-px w-full bg-border">
          <div
            className="h-px bg-foreground transition-[width] duration-500 ease-out"
            style={{ width: `${(progress / total) * 100}%` }}
          />
        </div>
      </div>

      <main className="mx-auto w-full max-w-[620px] px-gutter pt-12 pb-24">
        {children}
      </main>
    </div>
  )
}

function TextField({
  id,
  label,
  error,
  value,
  onChange,
  ...props
}: Omit<React.ComponentProps<typeof Input>, "onChange" | "value"> & {
  id: string
  label: string
  error?: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        name={id}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-[46px] border-border-strong bg-black/45 px-[18px] focus-visible:bg-black/70"
        )}
        {...props}
      />
      {error ? <FieldError id={`${id}-error`}>{error}</FieldError> : null}
    </Field>
  )
}

function FieldError({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <p id={id} role="alert" className="text-[0.8125rem] text-destructive">
      {children}
    </p>
  )
}

function Recap({ terme, valeur }: { terme: string; valeur: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 bg-card px-6 py-4">
      <dt className="font-heading text-xs tracking-brand text-faint uppercase">
        {terme}
      </dt>
      <dd className="text-[0.9375rem]">{valeur}</dd>
    </div>
  )
}

export { OnboardingFlow }
